/**
 * Return leg of a brokered OAuth2/OIDC or SAML sign-in.
 *
 * `GET /auth/v1/oauth/{provider}/callback` redirects here with the token bundle
 * in the URL *fragment* (never the query string, so tokens stay out of server
 * and proxy logs). This screen consumes the fragment, wipes it from history,
 * and hands off to the persona router — or to the MFA step when the persona
 * still owes a second factor.
 */
import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { useSession } from '../../app/session-context';
import type { TokenResponse } from '../../lib/api-client';
import { ROUTES } from '../../lib/constants';
import { decodeAccessToken } from '../../lib/jwt';
import { isPersona } from '../../lib/personas';
import { loadLastTeamSlug } from '../../lib/session-storage';

import { LoginMfaStep } from './login-mfa';

type CallbackState = 'working' | 'mfa' | 'failed';

function readFragment(): URLSearchParams {
  const hash = globalThis.location?.hash ?? '';
  return new URLSearchParams(hash.startsWith('#') ? hash.slice(1) : hash);
}

export function OAuthCallbackPage(): JSX.Element {
  const navigate = useNavigate();
  const { startSession } = useSession();
  const [state, setState] = useState<CallbackState>('working');
  const [error, setError] = useState<string | null>(null);
  const hasRunRef = useRef(false);

  useEffect(() => {
    if (hasRunRef.current) return;
    hasRunRef.current = true;

    const params = readFragment();
    // Clear the fragment so a back-navigation cannot replay the tokens.
    globalThis.history?.replaceState?.(null, '', globalThis.location?.pathname ?? ROUTES.login);

    const idpError = params.get('error');
    if (idpError !== null) {
      setError(params.get('error_description') || `The identity provider returned: ${idpError}`);
      setState('failed');
      return;
    }

    const accessToken = params.get('access_token');
    const refreshToken = params.get('refresh_token');
    const persona = params.get('persona');
    if (accessToken === null || refreshToken === null || !isPersona(persona)) {
      setError('This sign-in link is incomplete or has already been used. Start again.');
      setState('failed');
      return;
    }

    const claims = decodeAccessToken(accessToken);
    const tokens: TokenResponse = {
      access_token: accessToken,
      refresh_token: refreshToken,
      token_type: 'Bearer',
      expires_in: Number(params.get('expires_in') ?? '900'),
      session_id: params.get('session_id') ?? '',
      tenant_id: claims?.tid ?? null,
      persona,
      mfa_required: params.get('mfa_required') === 'true',
      // The callback fragment does not carry enrolment state; the MFA step
      // offers enrolment explicitly if the code cannot be produced.
      mfa_enrolled: true,
    };
    // `state` carries the team slug the sign-in started from (the token has no
    // slug claim); the last team used on this device is the fallback.
    startSession(tokens, params.get('state') || loadLastTeamSlug() || null);
    if (tokens.mfa_required) {
      setState('mfa');
      return;
    }
    navigate(ROUTES.postAuth, { replace: true });
  }, [navigate, startSession]);

  return (
    <div className="auth">
      <div className="auth__panel">
        {state === 'working' ? <p role="status">Completing sign-in…</p> : null}
        {state === 'mfa' ? (
          <LoginMfaStep
            isEnrolled
            onVerified={() => navigate(ROUTES.postAuth, { replace: true })}
            onCancel={() => navigate(ROUTES.login, { replace: true })}
          />
        ) : null}
        {state === 'failed' ? (
          <div className="stack">
            <div className="banner banner--error" role="alert">
              <span className="banner__title">Sign-in did not complete</span>
              <span>{error}</span>
            </div>
            <Link className="btn btn--primary btn--block" to={ROUTES.login}>
              Back to sign in
            </Link>
          </div>
        ) : null}
      </div>
    </div>
  );
}
